import { parseShippingTimeDays } from '~/utils/shop/parseShippingTimeDays';

export type ActionBarCrossSellVariant = {
  sku: string;
  name?: string | null;
  lengthLabel: string;
  priceValue: number | null;
  currency?: string | null;
  shippingTimeDays: number | null;
  imageJson?: string | null;
};

export type ActionBarCrossSellCandidateProduct = {
  /** Recommended-product SKU from the PDP dropdown order */
  sku: string;
  variants: ActionBarCrossSellVariant[];
};

export type ActionBarCrossSellSelection = {
  productSku: string;
  selectedVariantSku: string;
  variants: ActionBarCrossSellVariant[];
};

/**
 * Per-category selection: walk products in PDP order, pick the first whose
 * fastest known length is ≤ mainProductShippingDays (or any when main is unknown).
 */
export function selectActionBarCrossSellProduct(
  candidates: ReadonlyArray<ActionBarCrossSellCandidateProduct>,
  mainProductShippingDays: number | null
): ActionBarCrossSellSelection | null {
  for (const candidate of candidates) {
    if (!candidate.variants.length) {
      continue;
    }

    if (mainProductShippingDays == null) {
      return {
        productSku: candidate.sku,
        selectedVariantSku: candidate.variants[0].sku,
        variants: candidate.variants
      };
    }

    const fastestKnown = findFastestKnownVariant(candidate.variants);
    if (!fastestKnown) {
      // No known shipping times → show product with first variant rather than hide
      return {
        productSku: candidate.sku,
        selectedVariantSku: candidate.variants[0].sku,
        variants: candidate.variants
      };
    }

    if (
      fastestKnown.shippingTimeDays == null ||
      fastestKnown.shippingTimeDays <= mainProductShippingDays
    ) {
      return {
        productSku: candidate.sku,
        selectedVariantSku: fastestKnown.sku,
        variants: candidate.variants
      };
    }
  }

  return null;
}

function findFastestKnownVariant(
  variants: ReadonlyArray<ActionBarCrossSellVariant>
): ActionBarCrossSellVariant | null {
  let fastest: ActionBarCrossSellVariant | null = null;

  for (const variant of variants) {
    if (variant.shippingTimeDays == null) {
      continue;
    }
    if (
      fastest == null ||
      (fastest.shippingTimeDays != null &&
        variant.shippingTimeDays < fastest.shippingTimeDays)
    ) {
      fastest = variant;
    }
  }

  return fastest;
}

export function getCustomAttributeString(
  items:
    | ReadonlyArray<{
        __typename?: string;
        code?: string | null;
        value?: string | null;
      } | null>
    | null
    | undefined,
  code: string
): string | null {
  const match = items?.find((item) => item?.code === code);
  if (!match) {
    return null;
  }
  if (typeof match.value === 'string' && match.value.length > 0) {
    return match.value;
  }
  return null;
}

export function shippingDaysFromCustomAttributes(
  items:
    | ReadonlyArray<{ code?: string | null; value?: string | null } | null>
    | null
    | undefined
): number | null {
  return parseShippingTimeDays(
    getCustomAttributeString(items, 'shipping_time')
  );
}
